// An LRO keeps going on the backend regardless of the browser, but its polling
// handle lives only in component state - so a page refresh forgets the in-flight
// run and the completion refetch never fires ("I clicked it, refreshed, and it's
// gone"). Persist the handle in sessionStorage (per-tab, short-lived) so a reload
// re-attaches the poll and still refreshes the rows when the run finishes.
//
// Used by the inbox's landlord-research handle (keyed `leadId`) and by the
// degraded-source retry LRO, which has no row-key field at all because it covers a
// set of rows - hence the optional `rowKeyField`.
//
// Note what this is NOT: whether a row is being researched is read off the row's own
// `researchOperationKey` from the backend (see useLiveRuns), never off a
// persisted handle. Several runs go at once, so no single handle can answer it.

interface ResearchOpBase {
  operationKey: string;
  siteId: string;
}

// A per-lead research run caps at ~5 minutes; a persisted handle older than this
// is almost certainly a finished-and-reaped op, so ignore it rather than resume a
// poll that would leave a "researching" chip stuck forever. The timestamp is
// stamped at save time and lives only in storage (not in the component shape).
const RESEARCH_OP_TTL_MS = 30 * 60 * 1000;
const SAVED_AT_FIELD = "__savedAt";

// Load a persisted op, validating the two base string fields plus (when the caller
// has one) the page's own row-key field, and dropping anything past the TTL.
// Malformed / absent / expired -> null (start clean). The internal timestamp is
// stripped from the result.
export const loadPersistedResearchOp = <T extends ResearchOpBase>(
  storageKey: string,
  rowKeyField?: keyof T & string
): T | null => {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed.operationKey !== "string" ||
      typeof parsed.siteId !== "string" ||
      (rowKeyField !== undefined && typeof parsed[rowKeyField] !== "string")
    ) {
      return null;
    }
    const savedAt = parsed[SAVED_AT_FIELD];
    if (
      typeof savedAt === "number" &&
      Date.now() - savedAt > RESEARCH_OP_TTL_MS
    ) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }
    delete parsed[SAVED_AT_FIELD];
    return parsed as T;
  } catch {
    return null;
  }
};

export const savePersistedResearchOp = (
  storageKey: string,
  op: ResearchOpBase | null
): void => {
  try {
    if (op) {
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify({ ...op, [SAVED_AT_FIELD]: Date.now() })
      );
    } else {
      window.sessionStorage.removeItem(storageKey);
    }
  } catch {
    // sessionStorage unavailable (private mode / quota) - re-attach just won't
    // survive a refresh, same as before. Not worth surfacing an error.
  }
};
